<template>
  <div class="calendar-view">
    <!-- Header Component -->
    <header class="app-header">
      <div class="logo-container">
        <img
          src="../assets/StaffTrainingLogo.png"
          alt="Company Logo"
          class="company-logo"
        >
        <h1 class="header-title">
          Staff Training Inventory Management System
        </h1>
      </div>
      <div class="header-buttons">
        <button @click="navigateToAdminDashboard">
          Back to Dashboard
        </button>
        <button @click="logout">
          Logout
        </button>
      </div>
    </header>

    <!-- Main Content -->
    <div class="content">
      <h1>Calendar - Checked-Out Equipment</h1>
      <p>This page tracks all equipment that has been or is currently checked out</p>
      <div class="color-toggle">
        <label for="colorScheme">Color Events By:</label>
        <select
          id="colorScheme"
          v-model="colorScheme"
          @change="updateEventColors"
        >
          <option value="status">
            Status
          </option>
          <option value="staffName">
            Staff Name
          </option>
        </select>
      </div>
      <!-- VueCal Component -->
      <div class="calendar-container">
        <vue-cal
          :events="events"
          :time="true"
          overlap
          :time-from="8 * 60"
          :time-to="20 * 60"
          :time-step="60"
          :view="view"
          hide-weekends
          show-time-in-cells
          @event-click="onEventClick"
        />
      </div>
    </div>
    <div
      :class="{'modal-overlay': true, 'active': selectedEvent}"
      @click.self="closeModal"
    >
      <div
        v-if="selectedEvent"
        class="modal-content"
      >
        <h3>Event Details</h3>
        <p><strong>Title:</strong> {{ selectedEvent.title }}</p>
        <p><strong>Start Time:</strong> {{ selectedEvent.start }}</p>
        <p><strong>End Time:</strong> {{ selectedEvent.end }}</p>
        <p><strong>Action:</strong> {{ selectedEvent.action || 'N/A' }}</p>
        <p><strong>Quantity:</strong> {{ selectedEvent.quantity || 'N/A' }}</p>
        <p><strong>Transaction ID:</strong> {{ selectedEvent.transactionID || 'N/A' }}</p>
        <button @click="closeModal">
          Close
        </button>
      </div>
    </div>

    <!-- Footer -->
    <footer class="app-footer">
      <div class="footer-content">
        <img
          src="@/assets/ParentLogo.png"
          alt="Parent Company Logo"
          class="footer-logo"
        >
        <p>© 2024 Staff Nursing Ltd. All rights reserved.</p>
      </div>
    </footer>
  </div>
</template>

<script>
import axios from 'axios';
// eslint-disable-next-line import/no-extraneous-dependencies
import VueCal from 'vue-cal';
// eslint-disable-next-line import/no-extraneous-dependencies
import 'vue-cal/dist/vuecal.css';

export default {
  name: 'CalendarView',
  components: {
    VueCal,
  },
  data() {
    return {
      view: 'month', // Can be 'day', 'week', or 'month'
      events: [],
      selectedEvent: null, // To store the clicked event details
      colorScheme: 'status', // Default to coloring by status
    };
  },
  created() {
    this.fetchCheckedOutItems(); // for live check out events
    this.fetchHistoricalLogs(); // for resolved check outs
  },
  methods: {
    onEventClick(event) {
      if (!event) {
        console.error('No event received from click.');
        return;
      }
      // eslint-disable-next-line no-console
      console.log('Clicked event:', event);
      this.selectedEvent = {
        title: event.title,
        start: new Date(event.start).toLocaleString(),
        end: new Date(event.end).toLocaleString(),
        action: event.extendedProps?.action || 'N/A',
        quantity: event.extendedProps?.quantity || 'N/A',
        transactionID: event.extendedProps?.transactionID || 'N/A',
      };

      // eslint-disable-next-line no-console
      console.log('Event clicked:', this.selectedEvent); // Debugging log
    },
    closeModal() {
      this.selectedEvent = null;
    },
    updateEventColors() {
      this.events = this.events.map((event) => {
        const updatedEvent = { ...event };
        if (this.colorScheme === 'status') {
        // Assign class only to live (checked-out) events
          if (event.extendedProps.action === 'checkOut') {
            updatedEvent.class = 'event-out'; // Checked-out items
          } else {
            delete updatedEvent.class; // No class for historical logs
          }
        } else if (this.colorScheme === 'staffName') {
        // Color by staff name
          const staffName = event.extendedProps.checkedOutBy || 'Unknown';
          updatedEvent.class = `event-staff-${this.hashStringToColorClass(staffName)}`;
        }
        return updatedEvent;
      });
    },

    hashStringToColorClass(str) {
      const hash = Array.from(str).reduce((acc, char) => acc + char.charCodeAt(0), 0);
      return hash % 10; // Map to a number between 0-9 for color classes
    },
    navigateToAdminDashboard() {
      this.$router.push({ name: 'AdminDashboard' });
    },
    logout() {
      localStorage.removeItem('authToken');
      this.$router.push('/');
    },

    async fetchCheckedOutItems() {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getCheckedOutItems', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data.success) {
          const liveEvents = response.data.items.map((item) => {
            const start = new Date(item.lastCheckedOutTime);
            if (!start) return null;

            const staffName = item.checkedOutBy;

            const now = new Date();
            const end = new Date(start);
            if (now - start < 3600000) { // Less than 1 hour difference
              end.setHours(start.getHours() + 1);
            } else {
              end.setTime(now.getTime());
            }

            if (!end) return null; // Guard against null

            return {
              title: `${item.name} checked out by ${staffName}`,
              start,
              end,
              class: 'event-out', // Use the new class
              extendedProps: {
                action: 'checkOut',
                quantity: item.quantity || 1,
                checkedOutBy: item.checkedOutBy,
              },
            };
          }).filter((event) => event !== null);

          this.events = [...this.events, ...liveEvents];

          // eslint-disable-next-line no-console
          console.log('Live checked-out items added to events:', liveEvents);
        } else {
          console.warn('No checked-out items found.');
        }
      } catch (error) {
        console.error('Error fetching checked-out items:', error);
      }
    },

    async fetchHistoricalLogs() {
      try {
        const response = await axios.get('https://api.inventory.stafftraining.me/api/getCalendarData', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('authToken')}`,
          },
        });

        if (response.data.success) {
          const historicalEvents = response.data.data.map((log) => {
            const start = new Date(log.start_time);
            let end = log.end_time
              ? new Date(log.end_time)
              : null;

            if (end && end - start < 3600000) { // Less than 1 hour
              end = new Date(start);
              end.setHours(start.getHours() + 1);
            }

            if (!start || !end) return null; // Guard against null

            return {
              title: `${log.itemName} checked out by ${log.userFirstName} ${log.userLastName}`,
              start,
              end,
              extendedProps: {
                action: log.checkedInBy ? 'checkIn' : 'checkOut',
                quantity: log.quantity || 1,
                transactionID: log.transactionID,
                itemID: log.item_id,
                checkedOutBy: log.checkedOutBy,
                checkedInBy: log.checkedInBy || 'N/A',
              },
            };
          }).filter((event) => event !== null);

          this.events = [...this.events, ...historicalEvents];

          // eslint-disable-next-line no-console
          console.log('Historical events added to events:', historicalEvents);
        } else {
          console.error('Invalid response structure or no historical logs available.');
        }
      } catch (error) {
        console.error('Error fetching historical logs:', error.message);
      }
    },
  },
};
</script>

<style>
/* General Reset for Margin and Padding */
body, html {
  margin: 0;
  padding: 0;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
}

html, body {
  background-color: transparent !important; /* Force override any default styles */
}

/* Calendar View Container */
.calendar-view {
  display: flex;
  flex-direction: column;
  min-height: 100vh;
}

/* Header Styles */
.app-header {
  position: fixed; /* Keep the header fixed at the top */
  top: 0;
  left: 0;
  right: 0;
  height: 100px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 20px;
  background-color: #262626; /* Dark blue/gray color */
  color: white;
  z-index: 1000; /* Ensure header is above other content */
  width: 100%;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1); /* Adding subtle shadow for better separation */
}

.logo-container {
  display: flex;
  align-items: center;
}

.company-logo {
  height: 60px;
}

.header-title {
  margin-left: 20px;
  font-size: 1.5em;
}

.header-buttons {
  display: flex;
  gap: 20px; /* Space between buttons */
}

.header-buttons button {
  background-color: #00A3DB; /* Button color */
  color: white;
  border: none;
  padding: 8px 12px;
  cursor: pointer;
  border-radius: 12px;
}

.header-buttons button:hover {
  background-color: #2980b9; /* Darker blue on hover */
}

/* Main Content Container */
.content {
  flex-grow: 1;
  padding: 100px 20px 10px 20px;
  width: 90%;
  margin: 0 auto;
  position: relative;
  z-index: 1;
}

.calendar-container {
  margin-top: 10px;
  padding: 10px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.1);
}

/* Footer Styles */
.app-footer {
  width: 100%;
  background-color: #262626; /* Dark blue/gray color */
  color: white;
  text-align: center;
  padding: 20px 0;
  margin-top: auto; /* Add space above the footer */
  position: relative;
  z-index: 1;
}

.footer-content {
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
}

.footer-logo {
  height: 60px;
  margin-bottom: 10px;
}

/* VueCal Custom Styling */
.vuecal__menu {
  background-color: #191732;
  color: #ffffff
}
.vuecal__title-bar {
  background-color: #00A3DB;
  color: #191732;
}

.vuecal__event {
  cursor: pointer;
  border: 1px solid #444444 !important;
  border-radius: 8px !important;
  min-height: 40px !important;
  padding: 5px !important;
  font-size: 10px !important;
}

.vuecal__event-title {
  font-size: 12px !important;
  font-weight: bold !important;
}

.vuecal__event[style] {
  background-color: #6edbfff7;
  color: #191732 !important; /* Enforce text color to white */
}

.vuecal__event.event-out {
  background-color: #f44336; /* Red for items still out */
  color: #fff; /* White text for readability */
  border: 1px solid #c62828; /* Add a slightly darker border for better contrast */
}

.vuecal__event.event-staff-0 { background-color: #f4a261; color: #fff; }
.vuecal__event.event-staff-1 { background-color: #2a9d8f; color: #fff; }
.vuecal__event.event-staff-2 { background-color: #e9c46a; color: #000; }
.vuecal__event.event-staff-3 { background-color: #264653; color: #fff; }
.vuecal__event.event-staff-4 { background-color: #e76f51; color: #fff; }
.vuecal__event.event-staff-5 { background-color: #6a4c93; color: #fff; }
.vuecal__event.event-staff-6 { background-color: #ffa69e; color: #000; }
.vuecal__event.event-staff-7 { background-color: #3d5a80; color: #fff; }
.vuecal__event.event-staff-8 { background-color: #98c1d9; color: #000; }
.vuecal__event.event-staff-9 { background-color: #ee6c4d; color: #fff; }
.vuecal__event.event-staff-10 { background-color: #1d3557; color: #fff; }
.vuecal__event.event-staff-11 { background-color: #a8dadc; color: #000; }

/* Override default color if no specific class is matched */
.vuecal__event.event-default {
  background-color: #CCCCCC !important; /* Default grey color */
}

.vuecal {
  margin: 20px; /* Add some margin to create space around the calendar */
  z-index: 10;
  position: relative;
}
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  /* Initially hidden */
  visibility: hidden;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.modal-overlay.active {
  visibility: visible;
  pointer-events: auto;
  opacity: 1;
}

.modal-content {
  background: #fff;
  padding: 20px;
  border-radius: 8px;
  width: 300px;
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.3);
  text-align: left;
  z-index: 1001;
}
.modal-content h3 {
  margin-top: 0;
}

.modal-content button {
  display: block;
  margin: 20px auto 0 auto;
  padding: 8px 16px;
  background-color: #00a3db;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}

.modal-content button:hover {
  background-color: #2980b9;
}
@media (max-width: 600px), (max-height: 1180px) {
  .header-title {
    font-size: 0.7em;
  }
  .header-buttons {
  gap: 8px; /* Space between buttons */
  }
  .header-buttons button {
  padding: 4px 8px;
  font-size: 0.7em;
  }
}
</style>
